/* ==========================================================================
   EDME — Tabbed Product Carousel
   Click + Drag dengeli final sürüm
   ========================================================================== */

(function () {
  'use strict';

  const SELECTOR = '[data-edme-tabbed-product-carousel]';
  const DRAG_THRESHOLD = 12;

  function getActivePanel(section) {
    return section.querySelector('[data-edme-tpc-panel].is-active');
  }

  function getActiveCarousel(section) {
    const panel = getActivePanel(section);
    return panel ? panel.querySelector('[data-edme-tpc-carousel]') : null;
  }

  function getActiveTrack(section) {
    const carousel = getActiveCarousel(section);
    return carousel ? carousel.querySelector('[data-edme-tpc-track]') : null;
  }

  function getActivePrevBtn(section) {
    const carousel = getActiveCarousel(section);
    return carousel ? carousel.querySelector('[data-edme-tpc-prev]') : null;
  }

  function getActiveNextBtn(section) {
    const carousel = getActiveCarousel(section);
    return carousel ? carousel.querySelector('[data-edme-tpc-next]') : null;
  }

  function getScrollAmount(section, track) {
    const card = track?.querySelector('.edme-tabbed-product-carousel__item');
    const multiplier = parseInt(section.getAttribute('data-scroll-step'), 10) || 2;

    if (!card) return 300 * multiplier;

    const styles = window.getComputedStyle(track);
    const gap = parseFloat(styles.columnGap || styles.gap || 0) || 0;

    return (card.offsetWidth + gap) * multiplier;
  }

  function updateArrows(section) {
    const track = getActiveTrack(section);
    const prevBtn = getActivePrevBtn(section);
    const nextBtn = getActiveNextBtn(section);

    if (!track || !prevBtn || !nextBtn) return;

    const maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth);
    const currentLeft = track.scrollLeft;
    const threshold = 4;

    const atStart = currentLeft <= threshold;
    const atEnd = currentLeft >= maxScrollLeft - threshold;
    const noOverflow = maxScrollLeft <= threshold;

    prevBtn.disabled = noOverflow || atStart;
    nextBtn.disabled = noOverflow || atEnd;

    prevBtn.classList.toggle('is-disabled', noOverflow || atStart);
    nextBtn.classList.toggle('is-disabled', noOverflow || atEnd);
  }

  function activateTab(section, targetId) {
    const tabs = section.querySelectorAll('[data-edme-tpc-tab-trigger]');
    const panels = section.querySelectorAll('[data-edme-tpc-panel]');

    tabs.forEach((tab) => {
      const isActive = tab.getAttribute('data-target') === targetId;

      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');

      if (isActive) {
        tab.scrollIntoView({
          behavior: 'smooth',
          inline: 'nearest',
          block: 'nearest'
        });
      }
    });

    panels.forEach((panel) => {
      const isActive = panel.getAttribute('data-panel') === targetId;

      panel.classList.toggle('is-active', isActive);

      if (isActive) {
        panel.removeAttribute('hidden');

        const track = panel.querySelector('[data-edme-tpc-track]');
        if (track) track.scrollLeft = 0;
      } else {
        panel.setAttribute('hidden', '');
      }
    });

    window.requestAnimationFrame(() => {
      updateArrows(section);
    });
  }

  function bindDragScroll(section, track) {
    if (!track || track.dataset.dragBound === 'true') return;

    track.dataset.dragBound = 'true';

    let pointerDown = false;
    let isDragging = false;
    let startX = 0;
    let startLeft = 0;
    let activePointerId = null;

    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch') return;
      if (event.button !== 0) return;
      if (event.target.closest('button, input, select, textarea')) return;

      pointerDown = true;
      isDragging = false;
      startX = event.clientX;
      startLeft = track.scrollLeft;
      activePointerId = event.pointerId;
    });

    track.addEventListener('pointermove', (event) => {
      if (!pointerDown) return;

      const diff = event.clientX - startX;

      if (!isDragging && Math.abs(diff) < DRAG_THRESHOLD) {
        return;
      }

      if (!isDragging) {
        isDragging = true;
        track.classList.add('is-dragging');
        track.setPointerCapture?.(activePointerId);
      }

      event.preventDefault();
      track.scrollLeft = startLeft - diff;
    });

    const endDrag = (event) => {
      if (!pointerDown) return;

      pointerDown = false;

      if (isDragging) {
        track.dataset.wasDragged = 'true';

        window.setTimeout(() => {
          delete track.dataset.wasDragged;
        }, 0);
      }

      isDragging = false;
      activePointerId = null;
      track.classList.remove('is-dragging');

      try {
        track.releasePointerCapture?.(event.pointerId);
      } catch (err) {}

      window.requestAnimationFrame(() => {
        updateArrows(section);
      });
    };

    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    track.addEventListener('pointerleave', endDrag);

    track.addEventListener(
      'click',
      (event) => {
        if (track.dataset.wasDragged === 'true') {
          event.preventDefault();
          event.stopPropagation();
        }
      },
      true
    );
  }

  function bindTrack(section, track) {
    if (!track || track.dataset.scrollBound === 'true') return;

    track.dataset.scrollBound = 'true';

    bindDragScroll(section, track);

    track.addEventListener(
      'scroll',
      () => {
        window.requestAnimationFrame(() => updateArrows(section));
      },
      { passive: true }
    );
  }

  function initSection(section) {
    if (!section || section.dataset.edmeTabbedProductCarouselInit === 'true') return;

    section.dataset.edmeTabbedProductCarouselInit = 'true';

    const tabs = section.querySelectorAll('[data-edme-tpc-tab-trigger]');
    const tracks = section.querySelectorAll('[data-edme-tpc-track]');
    const prevButtons = section.querySelectorAll('[data-edme-tpc-prev]');
    const nextButtons = section.querySelectorAll('[data-edme-tpc-next]');

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const targetId = tab.getAttribute('data-target');
        if (targetId) activateTab(section, targetId);
      });
    });

    tracks.forEach((track) => {
      bindTrack(section, track);
    });

    prevButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const track = getActiveTrack(section);
        if (!track) return;

        track.scrollBy({
          left: -getScrollAmount(section, track),
          behavior: 'smooth'
        });
      });
    });

    nextButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const track = getActiveTrack(section);
        if (!track) return;

        track.scrollBy({
          left: getScrollAmount(section, track),
          behavior: 'smooth'
        });
      });
    });

    window.addEventListener(
      'resize',
      () => {
        window.requestAnimationFrame(() => updateArrows(section));
      },
      { passive: true }
    );

    updateArrows(section);
  }

  function initAll() {
    document.querySelectorAll(SELECTOR).forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector(SELECTOR);

    if (section) {
      section.dataset.edmeTabbedProductCarouselInit = 'false';
      initSection(section);
    }
  });
})();